import React from "react";
import { 
  Laptop, 
  Wrench, 
  Snowflake, 
  Car, 
  Sofa, 
  MonitorSpeaker, 
  Smartphone, 
  Bot, 
  Wifi, 
  Cable, 
  Package, 
  Settings, 
  CreditCard, 
  Server, 
  Battery, 
  Construction, 
  Stethoscope, 
  Sun, 
  Fuel,
  ShoppingCart 
} from "lucide-react";

export interface AssetType {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  category: string;
  color: string;
}

export const ASSET_CATEGORIES = {
  IT_TECHNOLOGY: "IT & Technology",
  INDUSTRIAL: "Industrial Equipment",
  INFRASTRUCTURE: "Infrastructure",
  SPECIALIZED: "Specialized Equipment",
  ENERGY_UTILITIES: "Energy & Utilities"
};

export const ASSET_TYPES: AssetType[] = [
  // IT & Technology
  { id: "laptop", name: "Laptop", icon: Laptop, category: ASSET_CATEGORIES.IT_TECHNOLOGY, color: "hsl(var(--primary))" },
  { id: "it-equipment", name: "IT Equipment", icon: MonitorSpeaker, category: ASSET_CATEGORIES.IT_TECHNOLOGY, color: "hsl(var(--primary))" },
  { id: "mobile", name: "Mobile", icon: Smartphone, category: ASSET_CATEGORIES.IT_TECHNOLOGY, color: "hsl(var(--primary))" },
  { id: "server", name: "Server", icon: Server, category: ASSET_CATEGORIES.IT_TECHNOLOGY, color: "hsl(var(--primary))" },
  { id: "networking-equipment", name: "Networking Equipment", icon: Wifi, category: ASSET_CATEGORIES.IT_TECHNOLOGY, color: "hsl(var(--primary))" },
  { id: "cables", name: "Cables", icon: Cable, category: ASSET_CATEGORIES.IT_TECHNOLOGY, color: "hsl(var(--primary))" },

  // Industrial Equipment
  { id: "crane", name: "Crane", icon: Construction, category: ASSET_CATEGORIES.INDUSTRIAL, color: "hsl(var(--warning))" },
  { id: "forklift", name: "Forklift", icon: Car, category: ASSET_CATEGORIES.INDUSTRIAL, color: "hsl(var(--warning))" },
  { id: "robots", name: "Robots", icon: Bot, category: ASSET_CATEGORIES.INDUSTRIAL, color: "hsl(var(--warning))" },
  { id: "plant-machinery", name: "Plant & Machinery", icon: Settings, category: ASSET_CATEGORIES.INDUSTRIAL, color: "hsl(var(--warning))" },
  { id: "cnc-machine", name: "CNC Machine", icon: Wrench, category: ASSET_CATEGORIES.INDUSTRIAL, color: "hsl(var(--warning))" },

  // Infrastructure
  { id: "furniture-fixtures", name: "Furniture & Fixtures", icon: Sofa, category: ASSET_CATEGORIES.INFRASTRUCTURE, color: "hsl(var(--secondary))" },
  { id: "freezer", name: "Freezer", icon: Snowflake, category: ASSET_CATEGORIES.INFRASTRUCTURE, color: "hsl(var(--secondary))" },

  // Specialized Equipment
  { id: "atm", name: "ATM", icon: CreditCard, category: ASSET_CATEGORIES.SPECIALIZED, color: "hsl(var(--accent))" },
  { id: "pos", name: "POS", icon: CreditCard, category: ASSET_CATEGORIES.SPECIALIZED, color: "hsl(var(--accent))" },
  { id: "vending-machines", name: "Vending Machines", icon: ShoppingCart, category: ASSET_CATEGORIES.SPECIALIZED, color: "hsl(var(--accent))" },
  { id: "medical-equipment", name: "Medical Equipment", icon: Stethoscope, category: ASSET_CATEGORIES.SPECIALIZED, color: "hsl(var(--accent))" },

  // Energy & Utilities
  { id: "solar", name: "Solar", icon: Sun, category: ASSET_CATEGORIES.ENERGY_UTILITIES, color: "hsl(var(--success))" },
  { id: "battery", name: "Battery", icon: Battery, category: ASSET_CATEGORIES.ENERGY_UTILITIES, color: "hsl(var(--success))" },
  { id: "energy-renewables", name: "Energy and Renewables", icon: Fuel, category: ASSET_CATEGORIES.ENERGY_UTILITIES, color: "hsl(var(--success))" },

  // Other
  { id: "other", name: "Other", icon: Package, category: "Other", color: "hsl(var(--muted-foreground))" }
];

export const getAssetTypeById = (id: string): AssetType | undefined => {
  return ASSET_TYPES.find(type => type.id === id);
};

export const getAssetTypeByName = (name: string): AssetType | undefined => {
  return ASSET_TYPES.find(type => type.name.toLowerCase() === name.toLowerCase());
};

export const getAssetTypesByCategory = (category: string): AssetType[] => {
  return ASSET_TYPES.filter(type => type.category === category);
};

// Asset Type Icon Component
export const AssetTypeIcon: React.FC<{
  assetType?: string;
  className?: string;
  color?: string;
}> = ({ 
  assetType, 
  className = "h-4 w-4",
  color,
}) => {
  const type = assetType ? getAssetTypeById(assetType) || getAssetTypeByName(assetType) : undefined;
  const IconComponent = type?.icon || Package;
  
  return (
    <IconComponent 
      className={className} 
      style={{ color: color || type?.color || "hsl(var(--muted-foreground))" }}
    />
  );
};
